<template>
    <section class="identity-view">
        <div class="identity-page-heading">
            <div>
                <p class="identity-page-kicker">IDENTITY &amp; PORTFOLIO</p>
                <h1>我的身份</h1>
<!--                <p>摄影师与化妆师身份独立管理，分别查看当前状态与申请历史摘要，互不影响。</p>-->
            </div>

        </div>

        <section v-if="campusPresentation.reminder" class="identity-campus-reminder" aria-live="polite">
            <span class="identity-campus-reminder-icon" aria-hidden="true">!</span>
            <button
                v-if="campusPresentation.reminder.dismissible"
                type="button"
                class="identity-campus-reminder-link"
                @click="openCampusCertification">
                <span>{{ campusPresentation.reminder.message }}</span>
                <i class="el-icon-arrow-right" aria-hidden="true"></i>
            </button>
            <span v-else class="identity-campus-reminder-message">{{ campusPresentation.reminder.message }}</span>
            <button
                v-if="campusPresentation.reminder.dismissible"
                type="button"
                class="identity-campus-reminder-dismiss"
                :disabled="dismissingCampusReminder"
                @click="dismissCampusReminder">不再提示</button>
        </section>

        <section class="identity-role-grid" aria-label="身份申请列表">
            <photographer-application-card
                v-if="photographerRole"
                :role="photographerRole"
                :has-campus-certification="campusPresentation.showCampusBadge"
                @submitted="$emit('photographer-submitted')"
                @open-materials="openMaterials('photographer')" />
            <makeup-artist-application-card
                v-if="makeupArtistRole"
                :role="makeupArtistRole"
                :has-campus-certification="campusPresentation.showCampusBadge"
                @submitted="$emit('makeup-artist-submitted')"
                @open-materials="openMaterials('makeup')" />
            <identity-role-card
                v-for="role in visibleRoles"
                :key="role.key"
                :role="role" />
        </section>

        <professional-materials-panel
            v-if="activeMaterialRole"
            :key="activeMaterialRole.key"
            :role="activeMaterialRole"
            :professional-type="activeProfessionalType"
            @close="closeMaterials"
            @updated="handleMaterialsUpdated" />

        <creator-professional-recovery-dialog
            v-if="activeRecovery"
            :key="activeRecovery.professionalType"
            :visible="true"
            :professional-type="activeRecovery.professionalType"
            :recovery="activeRecovery.recovery"
            @recovered="handleRecoverySubmitted"
            @recovery-state-changed="$emit('recovery-state-changed')"
            @recovery-access-denied="$emit('recovery-access-denied')" />

    </section>
</template>

<script>
import { getActiveRecovery } from "@/tool/recoveryFlow.cjs";
import campusCertification from "@/tool/campusCertification.cjs";
import API from "@/tool/api";
import request from "@/tool/request";
import {
    getResultData,
    getResultError,
    getResultMessage,
    isResultSuccess,
} from "@/tool/result";
import IdentityRoleCard from "./IdentityRoleCard";
import PhotographerApplicationCard from "./PhotographerApplicationCard";
import MakeupArtistApplicationCard from "./MakeupArtistApplicationCard";
import ProfessionalMaterialsPanel from "./ProfessionalMaterialsPanel";
import CreatorProfessionalRecoveryDialog from "./CreatorProfessionalRecoveryDialog";

export default {
    name: "IdentityView",
    components: {
        IdentityRoleCard,
        PhotographerApplicationCard,
        MakeupArtistApplicationCard,
        ProfessionalMaterialsPanel,
        CreatorProfessionalRecoveryDialog,
    },
    props: {
        roles: {
            type: Array,
            required: true,
        },
        recoveryQueue: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            activeMaterialKey: "",
            submittedRecoveryTypes: [],
            campusOverview: null,
            campusOverviewLoading: true,
            campusOverviewLoadFailed: false,
            dismissingCampusReminder: false,
        };
    },
    computed: {
        photographerRole() {
            return this.roles.find((role) => role.key === "photographer") || null;
        },
        makeupArtistRole() {
            return this.roles.find((role) => role.key === "makeup") || null;
        },
        visibleRoles() {
            return this.roles.filter((role) => role.key !== "photographer" && role.key !== "makeup");
        },
        activeMaterialRole() {
            if (this.activeMaterialKey === "photographer") return this.photographerRole;
            if (this.activeMaterialKey === "makeup") return this.makeupArtistRole;
            return null;
        },
        activeProfessionalType() {
            return this.activeMaterialKey === "photographer" ? "photographer" : "makeupArtist";
        },
        activeRecovery() {
            return getActiveRecovery(this.recoveryQueue, this.submittedRecoveryTypes);
        },
        campusPresentation() {
            if (this.campusOverviewLoading || this.campusOverviewLoadFailed || !this.campusOverview) {
                return { showCampusBadge: false, reminder: null };
            }
            return campusCertification.getCreatorCampusPresentation(this.campusOverview);
        },
    },
    methods: {
        loadCampusCertification() {
            this.campusOverviewLoading = true;
            this.campusOverviewLoadFailed = false;
            return request.get(API.certifications.campus.overview)
                .then((response) => {
                    if (!isResultSuccess(response)) {
                        throw getResultError(response, "校园认证信息加载失败");
                    }
                    this.campusOverview = campusCertification.normalizeCampusOverview(getResultData(response) || {});
                })
                .catch(() => {
                    this.campusOverview = null;
                    this.campusOverviewLoadFailed = true;
                })
                .finally(() => {
                    this.campusOverviewLoading = false;
                });
        },
        openCampusCertification() {
            this.$router.push({
                name: "basic",
                query: { openCampusCertification: "1" },
            });
        },
        dismissCampusReminder() {
            if (this.dismissingCampusReminder) return;
            this.dismissingCampusReminder = true;
            return request.post(API.certifications.campus.reminderDismissal)
                .then((response) => {
                    if (!isResultSuccess(response) || getResultData(response) !== true) {
                        throw getResultError(response, "校园认证提醒关闭失败");
                    }
                    this.campusOverview = {
                        ...this.campusOverview,
                        campusCertificationReminderDismissed: true,
                    };
                })
                .catch((error) => {
                    this.$message.error(getResultMessage(error, "校园认证提醒关闭失败，请稍后重试"));
                })
                .finally(() => {
                    this.dismissingCampusReminder = false;
                });
        },
        openMaterials(roleKey) {
            this.activeMaterialKey = roleKey;
        },
        closeMaterials() {
            this.activeMaterialKey = "";
        },
        handleMaterialsUpdated() {
            if (this.activeMaterialKey === "photographer") {
                this.$emit("photographer-submitted");
                return;
            }
            this.$emit("makeup-artist-submitted");
        },
        handleRecoverySubmitted(professionalType) {
            if (!this.activeRecovery || this.activeRecovery.professionalType !== professionalType) return;
            this.submittedRecoveryTypes.push(professionalType);
            this.$emit("recovery-submitted", professionalType);
        },
    },
    created() {
        this.loadCampusCertification();
    },
};
</script>

<style scoped>
.identity-page-heading {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 24px;
    margin-bottom: 36px;
}

.identity-page-kicker {
    margin: 0 0 8px;
    color: #b35ec0;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: .14em;
}

.identity-page-heading h1 {
    margin: 0;
    color: #3f3158;
    font-size: clamp(32px, 3vw, 44px);
    font-weight: 800;
    letter-spacing: -.05em;
    line-height: 1.08;
}

.identity-page-heading p:not(.identity-page-kicker) {
    max-width: 520px;
    margin: 12px 0 0;
    color: #746c7e;
    font-size: 15px;
    line-height: 1.8;
}

.identity-heading-caption {
    color: #a17b91;
    font-size: 12px;
    text-align: right;
    white-space: nowrap;
}

.identity-role-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 20px;
    margin-top: 4px;
}

.identity-campus-reminder {
    display: flex;
    align-items: center;
    gap: 14px;
    margin: 0 0 18px;
    padding: 13px 16px;
    color: #795536;
    background: #fff8e9;
    border: 1px solid #f2dfb1;
    border-radius: 14px;
}

.identity-campus-reminder-icon {
    display: inline-grid;
    width: 18px;
    height: 18px;
    flex: 0 0 auto;
    place-items: center;
    color: #fff;
    background: #d69b39;
    border-radius: 50%;
    font-size: 12px;
    font-weight: 800;
    line-height: 1;
}

.identity-campus-reminder-link {
    display: flex;
    min-width: 0;
    flex: 1 1 auto;
    align-items: center;
    justify-content: flex-start;
    gap: 3px;
    padding: 0;
    color: inherit;
    background: transparent;
    border: 0;
    cursor: pointer;
    font: inherit;
    font-size: 13px;
    font-weight: 700;
    text-align: left;
}

.identity-campus-reminder-link i {
    flex: 0 0 auto;
    font-size: 16px;
}

.identity-campus-reminder-message {
    min-width: 0;
    flex: 1 1 auto;
    font-size: 13px;
    font-weight: 700;
}

.identity-campus-reminder-dismiss {
    flex: 0 0 auto;
    padding: 5px 9px;
    color: #9a7a59;
    background: #fff;
    border: 1px solid #ead6ad;
    border-radius: 7px;
    cursor: pointer;
    font: inherit;
    font-size: 12px;
    white-space: nowrap;
}

.identity-campus-reminder-dismiss:hover:not(:disabled) {
    color: #795536;
    border-color: #d9b978;
}

.identity-campus-reminder-dismiss:disabled {
    cursor: wait;
    opacity: .65;
}

@media (max-width: 1250px) {
    .identity-role-grid {
        grid-template-columns: 1fr;
    }
}

.identity-footnote {
    display: flex;
    gap: 12px;
    align-items: flex-start;
    margin: 26px 0 0;
    padding: 16px 18px;
    color: #756d7e;
    background: #fff;
    border: 1px solid #eddfe3;
    border-radius: 14px;
    font-size: 12px;
    line-height: 1.7;
}

.identity-footnote::before {
    flex: 0 0 auto;
    color: #a35b8d;
    content: "审核说明";
    font-size: 11px;
    font-weight: 800;
    letter-spacing: .06em;
}
</style>
